import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import {
  liveSalary, livePayrollTotal, listAttendance, listEmployees,
  listVisits, listPayroll
} from "@/lib/data";
import { fmtINR, fmtDateTime, MONTHS } from "@/lib/utils-app";
import { Link } from "react-router-dom";
import AttendanceCalendar from "@/components/AttendanceCalendar";
import AdminAttendanceGrid from "@/components/AdminAttendanceGrid";
import MonthYearPicker from "@/components/MonthYearPicker";
import {
  Wallet, MapPinned, ChevronRight, Sparkles, TrendingUp, TrendingDown,
  Users, ClipboardCheck, Receipt, Coins, Trophy, ArrowUpRight
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar
} from "recharts";

const today = new Date();
const Y = today.getFullYear();
const M = today.getMonth() + 1;

export default function Dashboard() {
  const { user, isAdmin, isManager } = useAuth();
  if (!user) return null;
  const greet = today.getHours() < 12 ? "Good morning ☀️" : today.getHours() < 17 ? "Good afternoon 👋" : "Good evening 🌙";

  return (
    <div className="sk-page space-y-5 md:space-y-6" data-testid="dashboard">
      {/* Hero greeting — solid brand-blue */}
      <div className="relative rounded-2xl bg-[#1E3A8A] p-5 md:p-7 text-white shadow-md overflow-hidden">
        <div className="absolute left-0 right-0 bottom-0 h-1.5 bg-[#F97316]" />
        <div className="relative flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs md:text-sm font-medium text-[#FFE4D0]">{greet}</div>
            <h1 className="font-heading text-2xl md:text-4xl font-extrabold leading-tight mt-0.5">
              {user.name?.split(" ")[0] || "Hello"}
            </h1>
            <div className="text-xs md:text-sm text-white/80 mt-1">{today.toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div>
          </div>
          <div className="font-bangla text-sm md:text-lg italic max-w-[60%] text-right text-[#FFE4D0]">"ঘর নয়, স্বপ্ন সাজাই আমরা"</div>
        </div>
      </div>

      {(isAdmin || isManager) ? <AdminBlock /> : <EmployeeBlock userId={user.id} />}
    </div>
  );
}

/* ---------------- EMPLOYEE ---------------- */
function EmployeeBlock({ userId }) {
  const [period, setPeriod] = useState({ year: Y, month: M });
  const [salary, setSalary] = useState(null);
  const [att, setAtt] = useState([]);
  const [visits, setVisits] = useState({ today: 0, month: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const pad = String(period.month).padStart(2, "0");
      const monthStart = `${period.year}-${pad}-01`;
      const next = new Date(period.year, period.month, 1).toISOString().slice(0, 10);
      const todayISO = today.toISOString().slice(0, 10);
      const [sal, attRows, visMonth, visToday] = await Promise.all([
        liveSalary(userId, period.year, period.month),
        listAttendance({ employee_id: userId, date_from: monthStart, date_to: next }),
        listVisits({ employee_id: userId, date_from: monthStart, date_to: next }),
        listVisits({ employee_id: userId, date_from: todayISO }),
      ]);
      if (cancelled) return;
      setSalary(sal);
      setAtt(attRows);
      setVisits({ today: visToday.length, month: visMonth.length });
    })();
    return () => { cancelled = true; };
  }, [userId, period.year, period.month]);

  return (
    <>
      {/* Month/year picker */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Viewing period</div>
        <MonthYearPicker value={period} onChange={setPeriod} />
      </div>

      {/* LIVE salary HERO */}
      <LiveSalaryHero salary={salary} title={`🪙 My Salary · ${MONTHS[period.month]} ${period.year}`} subtitle={period.year === Y && period.month === M ? "Live till today — auto-calculated from attendance & ledger" : "Full-month summary"} />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatTile icon="✅" label="Present"      value={salary?.present_days || 0} hint={`${MONTHS[period.month]} ${period.year}`} color="navy" />
        <StatTile icon="🟡" label="Half-day"     value={salary?.half_days   || 0} hint={`${MONTHS[period.month]} ${period.year}`} color="lightOrange" />
        <StatTile icon="📍" label="Visits today" value={visits.today} hint={`${visits.month} in period`} color="orange" />
        <StatTile icon="💸" label="Advance"      value={fmtINR(salary?.advance || 0)} hint="outstanding" color="lightBlue" />
      </div>

      {/* Calendar + recent visits side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="sk-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-extrabold flex items-center gap-2"><span>📅</span> Attendance — {MONTHS[period.month]} {period.year}</h2>
          </div>
          <AttendanceCalendar year={period.year} month={period.month} records={att} />
        </div>
        <RecentVisitsCard userId={userId} />
      </div>
    </>
  );
}

/* ---------------- ADMIN / MANAGER ---------------- */
function AdminBlock() {
  const [agg, setAgg] = useState(null);
  const [empCount, setEmpCount] = useState(0);
  const [visits, setVisits] = useState([]);
  const [topPerf, setTopPerf] = useState([]);
  const [pay, setPay] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const monthStart = new Date(Y, M - 1, 1).toISOString().slice(0, 10);
      const [a, emps, vAll, payAll] = await Promise.all([
        livePayrollTotal(Y, M).catch(() => null),
        listEmployees({ status: "active" }).catch(() => []),
        listVisits({ date_from: monthStart }).catch(() => []),
        listPayroll({ year: Y, month: M }).catch(() => []),
      ]);
      if (cancelled) return;
      setAgg(a);
      setEmpCount(emps.length);
      setVisits(vAll);
      setPay(payAll);

      // Top performers by visits in this month
      const byEmp = {};
      vAll.forEach(v => {
        const k = v.employee_id;
        if (!k) return;
        byEmp[k] = byEmp[k] || { id: k, name: v.employee_name || "—", count: 0 };
        byEmp[k].count++;
      });
      setTopPerf(Object.values(byEmp).sort((a, b) => b.count - a.count).slice(0, 5));
    })();
    return () => { cancelled = true; };
  }, []);

  // Visits trend last 14 days
  const visitsTrend = (() => {
    const arr = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const c = visits.filter(v => (v.visit_date || "").slice(0, 10) === key).length;
      arr.push({ d: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }), v: c });
    }
    return arr;
  })();
  // Visit type breakdown for donut
  const visitTypes = (() => {
    const m = { lead: 0, project: 0 };
    visits.forEach(v => { m[v.visit_type] = (m[v.visit_type] || 0) + 1; });
    return Object.entries(m).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v }));
  })();
  const PIE_COLORS = ["#1E3A8A", "#F97316", "#4DA3FF", "#FFA94D", "#1E40AF", "#FED7AA"];

  return (
    <>
      {/* Admin live total payroll HERO */}
      <LiveSalaryHero
        salary={{
          base_salary: agg?.total_base, allowance: agg?.total_allowance,
          deductions: (agg?.total_deductions || 0) + (agg?.total_advance || 0),
          advance: agg?.total_advance, net_live: agg?.total_net,
          present_days: null, half_days: null,
        }}
        title={`💰 Total Payroll So Far · ${MONTHS[M]} ${Y}`}
        subtitle={`Live across ${agg?.employee_count || empCount} active employees`}
        admin
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatTile icon="👥" label="Employees"          value={empCount} hint="active" color="navy" />
        <StatTile icon="📍" label="Visits"             value={visits.length} hint="this month" color="orange" />
        <StatTile icon="🏗️" label="Project visits"     value={visits.filter(v => v.visit_type === "project").length} hint="this month" color="lightOrange" />
        <StatTile icon="🧾" label="Generated payslips" value={pay.length} hint={MONTHS[M]} color="lightBlue" />
      </div>

      {/* Team attendance grid */}
      <div className="sk-card p-5">
        <AdminAttendanceGrid days={14} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="sk-card p-5 lg:col-span-2">
          <h2 className="font-heading text-lg font-extrabold flex items-center gap-2 mb-3"><span>📈</span> Visits trend — last 14 days</h2>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={visitsTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="d" tick={{ fontSize: 10, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="v" stroke="#1E3A8A" strokeWidth={3} dot={{ r: 4, fill: "#F97316" }} activeDot={{ r: 6, fill: "#F97316" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="sk-card p-5">
          <h2 className="font-heading text-lg font-extrabold flex items-center gap-2 mb-3"><span>🍩</span> Visit type breakdown</h2>
          {visitTypes.length === 0 ? (
            <div className="h-56 grid place-items-center text-slate-400 text-sm">No visits yet</div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={visitTypes} dataKey="value" innerRadius={48} outerRadius={80} paddingAngle={2}>
                    {visitTypes.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {visitTypes.map((e, i) => (
              <span key={e.name} className="inline-flex items-center gap-1 text-slate-600 capitalize">
                <span className="w-2 h-2 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /> {e.name} ({e.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Top performers + recent visits */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="sk-card p-5">
          <h2 className="font-heading text-lg font-extrabold flex items-center gap-2 mb-3"><Trophy className="w-5 h-5 text-[#FFA94D]" /> Top performers</h2>
          {topPerf.length === 0 ? <div className="text-sm text-slate-400 py-4 text-center">No visits this month</div> : (
            <ul className="space-y-2">
              {topPerf.map((t, i) => (
                <li key={t.id} className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-extrabold text-white ${i === 0 ? "bg-[#FFA94D]" : i === 1 ? "bg-slate-400" : "bg-slate-300"}`}>{i + 1}</span>
                  <div className="flex-1 truncate font-semibold text-slate-800 text-sm">{t.name}</div>
                  <span className="sk-badge sk-badge-info">{t.count} visits</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-2 sk-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-extrabold flex items-center gap-2"><MapPinned className="w-5 h-5 text-[#4DA3FF]" /> Recent visits</h2>
            <Link to="/visits" className="text-xs text-[#4DA3FF] font-bold hover:underline flex items-center gap-1">View all <ChevronRight className="w-3 h-3" /></Link>
          </div>
          {visits.length === 0 ? <div className="text-sm text-slate-400 py-4 text-center">No visits this month</div> : (
            <ul className="divide-y divide-slate-100">
              {visits.slice(0, 5).map(v => (
                <li key={v.id}>
                  <Link to={`/visits/${v.id}`} className="flex items-center gap-3 py-3 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition">
                    {v.selfie_url ? <img src={v.selfie_url} alt="" className="w-11 h-11 rounded-lg object-cover" /> : <div className="w-11 h-11 rounded-lg bg-slate-100 grid place-items-center"><MapPinned className="w-5 h-5 text-slate-400" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900 truncate">{v.visit_type === "lead" ? v.lead_name : v.project_name || "Untitled"}</div>
                      <div className="text-xs text-slate-500">{v.employee_name} · {fmtDateTime(v.visit_date)}</div>
                    </div>
                    <span className={`sk-badge ${v.visit_type === "lead" ? "sk-badge-info" : "sk-badge-warning"}`}>{v.visit_type}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------------- shared bits ---------------- */
function LiveSalaryHero({ salary, title, subtitle, admin }) {
  const net = salary?.net_live || 0;
  const base = salary?.base_salary || 0;
  const add  = salary?.allowance || 0;
  const adv  = salary?.advance || 0;
  const ded  = salary?.deductions || 0;
  return (
    <div className="relative rounded-2xl bg-white border-2 border-[#1E3A8A]/10 overflow-hidden" data-testid="live-salary-hero">
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#F97316]" />
      <div className="p-5 md:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#1E3A8A]">{title}</div>
            <div className="font-heading text-3xl md:text-5xl font-extrabold text-[#1E3A8A] mt-1.5 tracking-tight">
              {fmtINR(net)}
            </div>
            <div className="text-xs md:text-sm text-slate-500 mt-1.5 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#F97316]" /> {subtitle}</div>
          </div>
          {!admin && (salary?.present_days != null) && (
            <div className="hidden md:block text-right bg-[#FFE4D0] rounded-xl p-3 min-w-[110px]">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#C2410C]">Days</div>
              <div className="font-heading text-2xl font-extrabold text-[#C2410C]">{salary.present_days || 0}<span className="text-[#FFA94D] text-lg">/{(salary.present_days || 0) + (salary.half_days || 0) + (salary.absent_days || 0)}</span></div>
              <div className="text-[10px] text-[#C2410C]/80">present this month</div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <Breakdown icon={<Coins className="w-4 h-4" />}        label="Base"       value={base} positive />
          <Breakdown icon={<TrendingUp className="w-4 h-4" />}   label="Allowance"  value={add}  positive />
          <Breakdown icon={<TrendingDown className="w-4 h-4" />} label="Advance"    value={adv}  negative />
          <Breakdown icon={<TrendingDown className="w-4 h-4" />} label="Deductions" value={ded}  negative />
        </div>
        <Link to="/ledger" className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-[#1E3A8A] hover:text-[#F97316] transition">
          See ledger <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

const Breakdown = ({ icon, label, value, positive, negative }) => (
  <div className={`rounded-xl p-3 border ${positive ? "bg-[#DBEAFE] border-[#4DA3FF]/40" : negative ? "bg-[#FFE4D0] border-[#FFA94D]/50" : "bg-slate-50 border-slate-100"}`}>
    <div className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${positive ? "text-[#1E3A8A]" : negative ? "text-[#C2410C]" : "text-slate-600"}`}>
      {icon} {label}
    </div>
    <div className={`font-heading text-base md:text-lg font-extrabold mt-0.5 ${positive ? "text-[#1E3A8A]" : negative ? "text-[#C2410C]" : "text-slate-800"}`}>
      {negative ? "−" : positive ? "+" : ""}{fmtINR(value)}
    </div>
  </div>
);

const TILE_STYLES = {
  navy:       "bg-[#1E3A8A] text-white border-[#1E3A8A]",
  orange:     "bg-[#F97316] text-white border-[#F97316]",
  lightBlue:  "bg-[#DBEAFE] text-[#1E3A8A] border-[#4DA3FF]/30",
  lightOrange:"bg-[#FFE4D0] text-[#C2410C] border-[#FFA94D]/40",
  white:      "bg-white text-[#1E3A8A] border-[#1E3A8A]/15",
};

const StatTile = ({ icon, label, value, hint, color = "lightBlue" }) => {
  const isDark = color === "navy" || color === "orange";
  return (
    <div className={`relative rounded-xl border p-3.5 md:p-4 ${TILE_STYLES[color] || TILE_STYLES.lightBlue}`}>
      <div className="flex items-start justify-between">
        <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-white/85" : "opacity-80"}`}>{label}</div>
        <div className="text-xl md:text-2xl">{icon}</div>
      </div>
      <div className="font-heading text-2xl md:text-3xl font-extrabold mt-1.5">{value}</div>
      {hint && <div className={`text-[11px] mt-0.5 ${isDark ? "text-white/75" : "opacity-70"}`}>{hint}</div>}
    </div>
  );
};

function RecentVisitsCard({ userId }) {
  const [visits, setVisits] = useState([]);
  useEffect(() => { listVisits({ employee_id: userId }).then(rows => setVisits(rows.slice(0, 5))).catch(() => {}); }, [userId]);
  return (
    <div className="sk-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-lg font-extrabold flex items-center gap-2"><MapPinned className="w-5 h-5 text-[#4DA3FF]" /> My visits</h2>
        <Link to="/visits" className="text-xs text-[#4DA3FF] font-bold hover:underline">All →</Link>
      </div>
      {visits.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">No visits yet</div> : (
        <ul className="space-y-2.5">
          {visits.map(v => (
            <li key={v.id}>
              <Link to={`/visits/${v.id}`} className="flex items-center gap-2.5 hover:bg-slate-50 -mx-2 px-2 py-1.5 rounded-lg">
                {v.selfie_url ? <img src={v.selfie_url} alt="" className="w-9 h-9 rounded-lg object-cover" /> : <div className="w-9 h-9 rounded-lg bg-slate-100" />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold text-slate-800 truncate">{v.visit_type === "lead" ? v.lead_name : v.project_name || "Untitled"}</div>
                  <div className="text-[10px] text-slate-500">{fmtDateTime(v.visit_date)}</div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
